import { useState } from "react";
import { ResultsDetailPanel } from "pages/global/ResultsPage/components/ResultsDetailPanel/ResultsDetailPanel";

import { PatientDetailCard } from "features/patients/components/PatientDetailCard";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { CommonResultDetails } from "features/results/components/CommonResultDetails/CommonResultDetails";
import { RadiologyResultDetails } from "features/results/components/RadiologyResultDetails/RadiologyResultDetails";
import { ResultDetails } from "features/results/components/ResultDetails/ResultDetails";
import { PatientResultsTable } from "features/results/patient/PatientResultsTable/PatientResultsTable";
import { findRelatedResult } from "features/results/utils/resultCorrection";

import s from "./PatientResultsPage.module.css";

// kind → ResultsDetailPanel tab (drives confirm endpoint + type label).
const KIND_TO_TAB = {
  lab: "laboratory",
  rad: "radiology",
  common: "all-results",
};

const PatientResultsPage = () => {
  const patientId = useCurrentPatientId();
  // { item, kind } — kind tells us which endpoint the row came from.
  const [selected, setSelected] = useState(null);
  // The table's current page, kept so the detail can link to a correction's counterpart.
  const [loadedResults, setLoadedResults] = useState([]);

  const { data: staffMember } = useStaffInfo();
  const { data: allStaffMembers } = useAllStaffMembers();

  const detailItem = selected?.item ?? null;
  const kind = selected?.kind;

  /*
   * The result this one corrects, or the correction that replaced it, when the table's current
   * page holds it (KIN-2089). Null otherwise, and the notice then states the relationship
   * without a link.
   */
  const relatedResult = detailItem
    ? findRelatedResult(detailItem, loadedResults)
    : null;

  const renderDetailContent = () => {
    if (!detailItem) return null;

    if (kind === "lab") {
      return <ResultDetails item={detailItem} staffMember={staffMember} />;
    }
    if (kind === "rad") {
      return <RadiologyResultDetails item={detailItem} />;
    }

    // Common-backed: pick by resultType (matches the global "All" tab).
    const resultType = (detailItem.resultType || "").toUpperCase();

    if (resultType.includes("LAB")) {
      return <ResultDetails item={detailItem} staffMember={staffMember} />;
    }
    if (resultType.includes("RAD")) {
      return <RadiologyResultDetails item={detailItem} />;
    }

    return <CommonResultDetails item={detailItem} />;
  };

  return (
    <div className={s.page}>
      <div className={s.wrapper}>
        <div className={s.firstColumnWrapper}>
          <PatientDetailCard />
        </div>

        <div className={s.secondColumnWrapper}>
          {/*
           * The list lives in the wide column now (KIN-2027). It used to sit in the narrow left
           * one, where the long type labels — "Radiology - US Pelvis and Renal" and the lab test
           * names — truncated, while the widest part of the screen held only a placeholder
           * illustration until a row was picked.
           *
           * Kept mounted and hidden rather than unmounted when a result is open: the search,
           * type filter, hide-reviewed toggle, page and rows-per-page all live inside
           * PatientResultsTable, so unmounting would reset every one of them and the GP would
           * come back from a result to the top of an unfiltered list.
           */}
          <div className={detailItem ? s.hidden : undefined}>
            <PatientResultsTable
              title="Results"
              patientId={patientId}
              onSelectItem={(item, itemKind) =>
                setSelected({ item, kind: itemKind })
              }
              selectedItemId={detailItem?.id}
              onResultsLoaded={setLoadedResults}
            />
          </div>

          {!!detailItem && (
            <div className={s.detailWrapper}>

              <ResultsDetailPanel
                item={detailItem}
                selectedTab={KIND_TO_TAB[kind] ?? "all-results"}
                staffMembers={allStaffMembers}
                hidePatientInfo
                onOpenRelatedResult={
                  relatedResult
                    ? () => setSelected({ item: relatedResult, kind })
                    : undefined
                }
                onBack={() => setSelected(null)}
                onConfirm={() =>
                  setSelected((prev) =>
                    prev
                      ? { ...prev, item: { ...prev.item, orderStatus: "CONFIRMED" } }
                      : prev
                  )
                }
              >
                {renderDetailContent()}
              </ResultsDetailPanel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PatientResultsPage };
